// Row of FilterGroup triggers + FilterBadges + clear-all. Collapses to an icon
// inside CollectionToolbar's compact mode when not the expanded side.

import React from 'react';
import { FilterIcon } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import FilterBadges, { type FilterBadgeItem } from './FilterBadges';
import FilterGroup, { type FilterOption } from './FilterGroup';
import { useCollectionToolbarContext } from './CollectionToolbarContext';

export type FilterGroupConfig = {
  id: string;
  label: string;
  options: FilterOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  searchPlaceholder?: string;
  searchable?: boolean; // default true; turn off for a short, fixed list
};

type FilterControlsProps = {
  groups: FilterGroupConfig[];
  activeItems: FilterBadgeItem[];
  onClearAll: () => void;
};

const FilterControls: React.FC<FilterControlsProps> = ({
  groups,
  activeItems,
  onClearAll,
}) => {
  const { isCompact, expanded, setExpanded } = useCollectionToolbarContext();
  const collapsed = isCompact && expanded !== 'filters';

  if (collapsed) {
    return (
      <Button
        variant="outline"
        size="sm"
        type="button"
        aria-label="Show filters"
        onClick={() => setExpanded('filters')}
      >
        <FilterIcon />
        {activeItems.length > 0 && (
          <Badge variant="secondary">{activeItems.length}</Badge>
        )}
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {groups.map((group) => (
        <FilterGroup
          key={group.id}
          label={group.label}
          options={group.options}
          selectedValues={group.selectedValues}
          onToggle={group.onToggle}
          searchPlaceholder={group.searchPlaceholder}
          searchable={group.searchable}
        />
      ))}
      <FilterBadges items={activeItems} />
      {activeItems.length > 0 && (
        <Button type="button" variant="ghost" size="sm" onClick={onClearAll}>
          Clear all
        </Button>
      )}
    </div>
  );
};

export default FilterControls;
